import { isSelectableSearchType } from '@lib/utils/searchTypeOptions';
import { debugStore } from '@stores/Debug';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';

import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

export const SearchParamsInURL = observer(function SearchParamsInURL() {
  const libraryManager = useLibraryManager();
  const librarySearch = libraryManager.multiLibSearch;
  const isInitialMount = useRef(true);

  const [searchParams, setSearchParams] = useSearchParams();

  // Effect to read from URL params - this runs first on mount
  useEffect(() => {
    if (isInitialMount.current && librarySearch.request) {
      librarySearch.setSearchRequest(null);
    }

    const libs = searchParams.get('libs');
    const text = searchParams.get('text');
    const moment = searchParams.get('moment');
    const media = searchParams.get('media');

    if (libs) {
      libraryManager.setSelectedLibraries(libs.split(','));
    }

    if (text) {
      librarySearch.setSearchText(text);
    }

    if (moment) {
      librarySearch.setSearchClip(moment, null, false);
    }

    if (media) {
      librarySearch.setMediaId(media);
    }

    // Mark initial mount as completed
    isInitialMount.current = false;
  }, [searchParams]);

  // Set the search mode from the URL, landing on the store's shared scope-aware
  // fallback when the param is missing or names a mode that isn't currently
  // selectable (e.g. 'unified' in an old bookmark while unified search is
  // disabled). Separate from the effect above so a debug-flag flip re-evaluates
  // the mode — the flag read is inside isSelectableSearchType, so it must be a
  // dependency here.
  //
  // This effect is also the SOLE self-heal for a stale 'unified' selection when
  // the debug flag flips off: ResultTypeSwitch deliberately has no reset effect
  // (it would double-reset and fire a spurious mode-change toast). Keep the
  // silent reset and the debugStore.showUnifiedSearch dependency — both are
  // load-bearing.
  useEffect(() => {
    const search = searchParams.get('search');
    librarySearch.setResultType(
      isSelectableSearchType(search) ? search : librarySearch.fallbackResultType,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, debugStore.showUnifiedSearch]);

  // Effect to update URL params - this will skip the first run
  useEffect(() => {
    // Skip on initial mount to give the above effect higher priority
    if (isInitialMount.current) return;

    const libs = Array.from(libraryManager.selectedLibraryIdsToSearch).join(',');
    const text = librarySearch.request?.text?.trim();
    const moment = librarySearch.request?.clipId;
    const search = librarySearch.resultType;
    const media = librarySearch.mediaId;

    const params = searchParams;
    if (libs) params.set('libs', libs);
    if (media) {
      params.set('media', media);
    } else {
      params.delete('media');
    }

    if (text) {
      params.set('text', text);
    } else {
      params.delete('text');
    }

    if (moment) {
      params.set('moment', moment);
    } else {
      params.delete('moment');
    }

    // Always set the search mode in URL
    params.set('search', search);

    setSearchParams(params);
  }, [
    libraryManager.selectedLibrariesToSearch,
    librarySearch.request?.text,
    librarySearch.request?.clipId,
    librarySearch.resultType,
    librarySearch.mediaId,
  ]);

  return null;
});
